import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { SubTitle, Title } from './Title';
import { SectionWrapper } from './SectionWrapper';
import API_URL from '../../config'
// import Spinner from './Spinner';

const Wrapper = styled.div`
  padding: 5rem 7rem;
  /* margin-top: 50px; */
  color: #111;
  /* background-color: #111; */
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    padding: 16px 0 0 0;
  }
`;

const FormImage = styled.div`
  width: 30%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 7rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  /* filter: hue-rotate(270deg) brightness(90%) saturate(70%); */
  filter: saturate(0) contrast(1.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const Input = styled.input`
  color: var(--color-grey-50);
  background-color: transparent;
  padding: 9px 0;
  margin-bottom: 1.2rem;
  border: none;
  border-bottom: 1px solid #333;
  width: 100%;
  transition: border-color 1s;
  position: relative;
  &:focus {
    outline: none;
    border-bottom-color: var(--color-grey-50);
  }
  &::placeholder {
    transition: transform 0.5s, color 0.5s;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  &:focus::placeholder {
    color: var(--color-grey-50);
    font-size: 1rem;
    transform: translateY(-2.1rem); 
  }
`;

const TextArea = styled.textarea`
  color: var(--color-grey-50);
  background-color: transparent;
  padding: 9px 0;
  margin-bottom: 3rem;
  border: none;
  border-bottom: 1px solid #333;
  width: 100%;
  transition: border-color 1s;
  position: relative;
  &:focus {
    outline: none;
    border-bottom-color: var(--color-grey-50);
  }
  &::placeholder {
    transition: transform 0.5s, color 0.5s;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  &:focus::placeholder {
    color: var(--color-grey-50);
    font-size: 1rem;
    transform: translateY(-5.4rem); 
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  font-size: 16px;
  background-color: var(--color-brand-900);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background-color 0.4s ease;

  &:hover {
    background-color: var(--color-brand-700);
  }
`;

const animate = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const ButtonAnimation = styled.div`
  width: 20px;
  height: 20px;
  background-color: #fff;
  border-radius: 50%;
  animation: ${animate} 1s infinite;
`;

const ErrorMsg = styled.span`
  color: var(--color-red-700);
  margin-left: 1rem;
`;

const TextAreaErrorMsg = styled.span`
  color: var(--color-red-700);
  margin-left: 1rem;
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required!'),
  email: Yup.string().email('Invalid email').required('Email is required!'),
  subject: Yup.string().required('Subject is required!'),
  message: Yup.string().required('Message is required!'),
});

const SuccessMessage = styled.div`
  text-align: center;
  padding: 5px;
  color: lightgreen;
`

const ContactMe = () => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Set the URIs based on the environment
  // const netlifyUri = '/.netlify/functions/send-email';
  // const localUri = 'http://localhost:3000/send-email';

  const sendEmail = async (sanitizedData) => {
    try {
      const response = await fetch(`${API_URL}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sanitizedData)
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSuccessMessage('Email sent successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          navigate('/'); // Redirect to homepage
        }, 3000); // Redirect after 3 seconds
      } else {
        throw new Error('Failed to send email!');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred while sending email');
    }
  };

  const onSubmit = (formData, e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Sanitize input
    const sanitizedData = {
      name: DOMPurify.sanitize(formData.name),
      email: DOMPurify.sanitize(formData.email),
      subject: DOMPurify.sanitize(formData.subject),
      message: DOMPurify.sanitize(formData.message),
    };

    // console.log('Sanitized Form Data:', sanitizedData);

    // Send email
    sendEmail(sanitizedData);

    // Clear the form
    reset();
  };

  return (
    <SectionWrapper>
      <Title align='center'>Contact me</Title>
      {/* <SubTitle align="center">Passionate about turning ideas into reality with innovative design and development</SubTitle> */}
      <Wrapper>
        <FormImage>
          <Image src='/email.gif' />
        </FormImage>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Input
            type="text"
            id="name"
            name="name"
            placeholder="Name"
            {...register('name')}
          />
          {errors.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
          <Input
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            {...register('email')}
          />
          {errors.email && <ErrorMsg>{errors.email.message}</ErrorMsg>}
          <Input
            type="text"
            id="subject"
            name="subject"
            placeholder="Subject"
            {...register('subject')}
          />
          {errors.subject && <ErrorMsg>{errors.subject.message}</ErrorMsg>}
          <TextArea
            id="message"
            name="message"
            rows="4"
            placeholder="Message"
            {...register('message')}
          />
          {errors.message && <TextAreaErrorMsg>{errors.message.message}</TextAreaErrorMsg>}
          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <ButtonAnimation /> : 'Submit'}
          </Button>
        </Form>
      </Wrapper>
    </SectionWrapper>
  );
};

export default ContactMe;
